/* -------------------------------------------

	Name:		Syncer
	Date:		2026/03/12
	Author:		http://psdhtml.me

---------------------------------------------  */
"use strict";

const htmlTag = document.documentElement;
htmlTag.classList.add('js');


// Detects if the device is a mobile or touch-enabled device.
const isMobile = window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
htmlTag.classList.add(isMobile ? 'mobile' : 'no-mobile');


// Returns a filepath from window.filepaths by key, or falls back to the local path.
const fp = (localPath, key) => (window.filepaths && window.filepaths[key]) || localPath;


// Checks if the current page is NOT a fully initialized Shopify store.
const isNotShopify = () => window.Shopify === undefined || Object.keys(window.Shopify).length < 3;


// Checks if Shopify Theme Editor (designMode) is active on this page.
const isShopifyDesignMode = () => {
	return typeof window.Shopify !== 'undefined' && !!window.Shopify.designMode;
};


/*! Helper Functions -------------------------------------------------- */

// Returns a throttled version of a function that executes at most once per specified delay.
const throttle = function (callback, delay) {
	let timeoutId;

	return function () {
		if (!timeoutId) {
			timeoutId = setTimeout(function () {
				callback();
				timeoutId = null;
			}, delay);
		}
	};
};


// Schedules the callback to run when the browser is idle (using requestIdleCallback when available, falling back to a short setTimeout).
const runWhenIdle = (fn) => {
	if ('requestIdleCallback' in window) {
		requestIdleCallback(fn, {
			timeout: 2000
		});
	} else {
		setTimeout(fn, 300);
	}
};


// Loads an external JS file once and resolves when the script is available.
const loadJS = function (url, id, isModule) {
	return new Promise(function (resolve, reject) {
		let script = null;

		if (id) {
			script = document.getElementById(id);

			if (script) {
				if (script.dataset.loaded === 'true') {
					resolve();
					return;
				}

				script.addEventListener('load', resolve, {
					once: true
				});
				script.addEventListener('error', reject, {
					once: true
				});
				return;
			}
		}

		script = document.createElement('script');
		script.src = url;
		script.async = true;

		if (isModule) {
			script.type = 'module';
		}

		if (id) {
			script.id = id;
		}

		script.onload = function () {
			script.dataset.loaded = 'true';
			resolve();
		};

		script.onerror = function () {
			reject(new Error('Failed to load script: ' + url));
		};

		document.head.appendChild(script);
	});
};


// Injects a CSS file into the document.
const loadCSS = (id, href, media = 'screen') => {
	if (document.getElementById(id)) return;

	const link = document.createElement('link');

	link.setAttribute('id', id);
	link.setAttribute('rel', 'stylesheet');
	link.setAttribute('href', href);
	link.setAttribute('media', media);

	const css = document.querySelectorAll('link[id]');
	if (css.length) {
		css[css.length - 1].after(link);
	} else {
		document.head.appendChild(link);
	}
};


// Returns all elements matching a selector within a given scope, including the scope element itself.
const getScopedElements = (scope, selector) => {
	const elements = [];

	if (scope.matches && scope.matches(selector)) {
		elements.push(scope);
	}

	for (const el of scope.querySelectorAll(selector)) {
		elements.push(el);
	}

	return elements;
};


// Ensures the given initializer runs only once per global key.
const runOnce = function (key, fn) {
	if (window[key]) {
		return;
	}

	window[key] = true;
	fn();
};


// Runs a callback immediately if the DOM is ready, or waits for DOMContentLoaded.
const runOnReady = function (fn) {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', fn);
	} else {
		fn();
	}
};


// Runs an initializer on the document and again for dynamically loaded Shopify sections.
const runScopedInit = function (init) {
	runOnReady(function () {
		init(document);
	});

	document.addEventListener('shopify:section:load', function (e) {
		init(e.target);
	});
};


// Loads async theme scripts after the main document is ready.
runOnReady(function () {
	/*const backend_listeners_js = fp('assets/backend-listeners.js', 'a_js_backend_listeners');
	if (isShopifyDesignMode()) {
		loadJS(backend_listeners_js, 'js-backend-listeners');
	}*/

	const custom_async_js = fp('assets/custom-async.js', 'a_js_custom_async');
	runWhenIdle(function () {
		loadJS(custom_async_js, 'js-custom-async');
	});
});


// [data-sal] - initializes scroll animations by asynchronously loading the JS and CSS.
const initDataSal = async function (scope) {
	const dataSal = getScopedElements(scope, '[data-sal]');
	if (!dataSal.length) return;

	const animations_js = fp('assets/plugin-animations.js', 'a_js_animations');
	const animations_css = fp('assets/async-animations.css', 'a_css_animations');

	await loadJS(animations_js, 'plugin-animations');
	loadCSS('animations-css', animations_css);

	if (typeof sal !== 'function') return;

	sal({
		once: true
	});
};

runScopedInit(initDataSal);


// video.lazy - lazily loads videos by swapping data-src -> src for <source> elements and calling load() when the video enters the viewport (via IntersectionObserver).
function loadLazyVideo(video) {
	const sources = video.querySelectorAll('source[data-src]');
	for (let i = 0; i < sources.length; i++) {
		const source = sources[i];
		source.src = source.getAttribute('data-src');
		source.removeAttribute('data-src');
	}

	video.load();
	video.classList.remove('lazy');
	video.removeAttribute('data-lazy-video-ready');

	const p = video.play();
	if (p && typeof p.catch === 'function') p.catch(function () {});
}

function schedule(fn, delayMs) {
	delayMs = typeof delayMs === 'number' ? delayMs : 0;

	const run = function () {
		if (delayMs > 0) setTimeout(fn, delayMs);
		else fn();
	};

	if ('requestIdleCallback' in window) {
		requestIdleCallback(run, { timeout: 1200 });
		return;
	}

	requestAnimationFrame(run);
}

const initLazyVideo = function (scope) {
	const lazyVideos = getScopedElements(scope, 'video.lazy:not([data-lazy-video-ready])');
	if (!lazyVideos.length) return;

	if (!('IntersectionObserver' in window)) {
		for (let i = 0; i < lazyVideos.length; i++) {
			lazyVideos[i].setAttribute('data-lazy-video-ready', '1');

			schedule(function () {
				loadLazyVideo(lazyVideos[i]);
			}, 200);
		}
		return;
	}

	const lazyVideoObserver = new IntersectionObserver(function (entries, observer) {
		for (let i = 0; i < entries.length; i++) {
			const entry = entries[i];
			if (!entry.isIntersecting) continue;

			const video = entry.target;

			schedule(function () {
				loadLazyVideo(video);
			}, 200);

			observer.unobserve(video);
		}
	}, {
		rootMargin: '300px 0px'
	});

	for (let i = 0; i < lazyVideos.length; i++) {
		lazyVideos[i].setAttribute('data-lazy-video-ready', '1');
		lazyVideoObserver.observe(lazyVideos[i]);
	}
};

runScopedInit(initLazyVideo);


// .localization-form - submits Shopify localization forms instead of following language/country links.
const initLocalizationForms = function (scope) {
	const links = getScopedElements(scope, '.localization-form a[data-value]:not([data-localization-ready])');

	for (let i = 0; i < links.length; i++) {
		const link = links[i];

		link.setAttribute('data-localization-ready', '1');

		link.addEventListener('click', function (e) {
			e.preventDefault();

			const form = link.closest('form');
			if (!form) return;

			let input = null;

			if (link.hasAttribute('hreflang') || link.hasAttribute('lang')) {
				input = form.querySelector('input[name="locale_code"]');
			} else {
				input = form.querySelector('input[name="country_code"]');
			}

			if (!input) return;

			const returnTo = form.querySelector('input[name="return_to"]');

			if (returnTo) {
				returnTo.value = window.location.pathname + window.location.search;
			}

			input.value = link.dataset.value;
			form.submit();
		});
	}
};
runScopedInit(initLocalizationForms);
/*!*/